import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { iTopFiveApiV2EndpointsTopFiveImportImportTopFiveEndpoint } from '@/api/api.generated';
import type { ImportTopFiveResponse, ImportProgress } from '../types/import.types';
import type { ITopFiveApiV2EndpointsTopFiveImportImportTopFiveEndpointBody } from '@/api/models';

/**
 * Custom hook for CSV TopFive import with file upload progress tracking.
 * Uses generated API client from OpenAPI spec.
 * 
 * Features:
 * - Multipart form data upload via generated API client
 * - Real-time upload progress tracking
 * - Error handling with detailed error information
 * - TypeScript type safety with generated types
 * 
 * @example
 * ```tsx
 * const { importTopFive, isUploading, progress, error } = useImportTopFive();
 * 
 * const handleSubmit = async (file: File) => {
 *   const result = await importTopFive(file);
 *   if (result && !result.hasErrors) {
 *     console.log('Import successful!');
 *   }
 * };
 * ```
 */
export const useImportTopFive = () => {
  const [progress, setProgress] = useState<ImportProgress>({ loaded: 0, total: 0, percentage: 0 });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ file, startDate, endDate }: { file: File; startDate: string; endDate: string }) => {
      setError(null);
      setProgress({ loaded: 0, total: 0, percentage: 0 });

      const body: ITopFiveApiV2EndpointsTopFiveImportImportTopFiveEndpointBody = {
        file: file,
        startDate: startDate,
        endDate: endDate,
      };

      const response = await iTopFiveApiV2EndpointsTopFiveImportImportTopFiveEndpoint(
        body,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            if (total) {
              const percentage = Math.round((loaded * 100) / total);
              setProgress({ loaded, total, percentage });
            }
          },
        }
      );

      return response.data as ImportTopFiveResponse;
    },
    onError: (err: AxiosError<ImportTopFiveResponse>) => {
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.globalErrors && errorData.globalErrors.length > 0) {
          setError(errorData.globalErrors[0] || 'Import failed');
        } else if (errorData.summaryMessage) {
          setError(errorData.summaryMessage);
        } else {
          setError('Import failed. Please try again.');
        }
      } else {
        setError(err.message || 'Network error. Please check your connection.');
      }
    },
  });

  const importTopFive = async (file: File, startDate: string, endDate: string): Promise<ImportTopFiveResponse | null> => {
    try {
      const result = await mutation.mutateAsync({ file, startDate, endDate });
      return result;
    } catch (err) {
      // Error already handled in onError
      const axiosError = err as AxiosError<ImportTopFiveResponse>;
      if (axiosError.response?.data) {
        return axiosError.response.data;
      }
      return null;
    }
  };

  const resetState = () => {
    mutation.reset();
    setProgress({ loaded: 0, total: 0, percentage: 0 });
    setError(null);
  };

  return {
    importTopFive,
    isUploading: mutation.isPending,
    progress,
    error,
    resetState,
  };
};
